import { FieldError, useForm } from "react-hook-form";
import { translate } from "@kaltura/mediaspace-shared-utils";
import React, { ForwardRefRenderFunction, Ref, useEffect, useImperativeHandle } from "react";
import { TextField } from "@kaltura/mediaspace-shared-ui/form";
import { FormData, LiveFeature } from "../RestreamDestinationsTypes";
import { generateErrorMessage, notOnlySpaces } from "./helpers";

interface RestreamModalFormProps {
    formRef: Ref<RestreamModalFormHandle>;
    formData: FormData;
    setFormData: React.Dispatch<React.SetStateAction<FormData>>;
    setIsEmptyForm: React.Dispatch<React.SetStateAction<boolean>>;
    restreams: LiveFeature[];
    selectedStream?: LiveFeature;
    action: string;
}

export type RestreamModalFormHandle = {
    submitForm: () => void;
};

/**
 *  Restream Modal form content
 */
const RestreamModalForm: ForwardRefRenderFunction<RestreamModalFormHandle, RestreamModalFormProps> = (props, ref) => {
    const { formRef, formData, setFormData, setIsEmptyForm, restreams, selectedStream } = props;

    const {
        register,
        handleSubmit,
        formState: { isDirty, errors },
        watch,
    } = useForm<FormData>({
        criteriaMode: "all",
        reValidateMode: "onChange",
        mode: "all",
    });

    const watchAllFields = watch();

    const isUnique = (systemName: string) => {
        let isUnique;
        if (selectedStream) {
            isUnique = !restreams.some((stream) => {
                return stream.systemName.toLowerCase() === systemName.toLowerCase() && stream.id !== selectedStream.id;
            });
        }
        else {
            isUnique = !restreams.some((stream) => {
                return stream.systemName.toLowerCase() === systemName.toLowerCase();
            });
        }
        return isUnique;
    };

    const onSubmit = (data: FormData) => {
        if (isDirty) {
            setFormData(data);
        }
    };

    useImperativeHandle(
        formRef ?? ref,
        () => ({
            submitForm() {
                handleSubmit(onSubmit)();
            },
        })
    );

    useEffect(() => {
        const isEmptyForm =
            watchAllFields.systemName === ""
            && watchAllFields.primaryUrl === ""
            && watchAllFields.secondaryUrl === ""
            && watchAllFields.streamKey === "";
        setIsEmptyForm(isEmptyForm);
    }, [watchAllFields, setIsEmptyForm]);

    return (
        <form className="kms-ds-restreams-form">
            <TextField
                title={translate("System name")}
                placeholder={translate("e.g. YouTube, Facebook Live, Partner CDN")}
                required={true}
                error={errors.systemName as FieldError}
                errorMessage={generateErrorMessage((errors?.systemName as FieldError)?.type)}
                {...register(
                    "systemName",
                    {
                        required: true,
                        validate: {
                            isUnique,
                            notOnlySpaces,
                        },
                    }
                )}
                defaultValue={formData.systemName}
            />
            <TextField
                title={translate("Primary RTMP URL")}
                required={true}
                error={errors.primaryUrl as FieldError}
                errorMessage={
                    (errors.primaryUrl as FieldError)?.type === "pattern"
                        ? (errors.primaryUrl as FieldError).message
                        : translate("Field is required")
                }
                {...register(
                    "primaryUrl",
                    {
                        required: true,
                        pattern: {
                            value: /^rtmps?:\/\//,
                            message: translate("Must be a rtmp/rtmps url"),
                        },
                    }
                )}
                defaultValue={formData.primaryUrl}
            />
            <TextField
                title={translate("Secondary RTMP URL")}
                description={translate("Optional backup endpoint (if provided by the destination)")}
                error={errors.secondaryUrl as FieldError}
                errorMessage={(errors.secondaryUrl as FieldError)?.message}
                {...register(
                    "secondaryUrl",
                    {
                        pattern: {
                            value: /^rtmps?:\/\//,
                            message: translate("Must be a rtmp/rtmps url"),
                        },
                    }
                )}
                defaultValue={formData.secondaryUrl}
            />
            <TextField
                title={translate("Destination stream key")}
                description={translate("Provided by the destination platform (e.g. YouTube, Facebook)")}
                required={true}
                error={errors.streamKey as FieldError}
                errorMessage={translate("Field is required")}
                {...register("streamKey", { required: true })}
                defaultValue={formData.streamKey}
            />
        </form>
    );
};

export default React.forwardRef(RestreamModalForm);
